/**
 * Toast + ToastProvider.
 *
 * Minimal imperative toast hub:
 *   - ToastProvider wraps the app tree and renders a live region
 *   - useToast() returns `toast.{success,info,warning,danger}(message)`
 *
 * a11y: uses role="status" (polite) so it doesn't steal focus.
 */
import type { JSX } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ToastKind = "success" | "info" | "warning" | "danger";

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: ReactNode;
  /** ms before auto-dismiss. Default 4500. 0 = sticky. */
  duration?: number;
}

interface ToastContextValue {
  push: (t: Omit<ToastItem, "id">) => string;
  dismiss: (id: string) => void;
  success: (message: ReactNode, duration?: number) => string;
  info: (message: ReactNode, duration?: number) => string;
  warning: (message: ReactNode, duration?: number) => string;
  danger: (message: ReactNode, duration?: number) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastEntry extends ToastItem {
  expiresAt: number | null;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    // Effects own their timers, including StrictMode cleanup/replay. Absolute
    // deadlines prevent another toast's updates from extending an expiry.
    const timers = items.flatMap((item) => item.expiresAt === null ? [] : [
      setTimeout(() => dismiss(item.id), Math.max(0, item.expiresAt - performance.now())),
    ]);
    return () => timers.forEach(clearTimeout);
  }, [items, dismiss]);

  const push = useCallback(
    (t: Omit<ToastItem, "id">) => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
      const dur = t.duration ?? 4500;
      const item: ToastEntry = {
        id,
        ...t,
        expiresAt: dur > 0 ? performance.now() + dur : null,
      };
      setItems((cur) => [...cur, item]);
      return id;
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      dismiss,
      success: (m, d) => push({ kind: "success", message: m, duration: d }),
      info: (m, d) => push({ kind: "info", message: m, duration: d }),
      warning: (m, d) => push({ kind: "warning", message: m, duration: d }),
      danger: (m, d) => push({ kind: "danger", message: m, duration: d }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="cp-toast-region" role="region" aria-label="Notifications">
        {items.map((t) => (
          <div
            key={t.id}
            className={`cp-toast cp-toast-${t.kind}`}
            role="status"
            aria-live="polite"
          >
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
              style={{
                background: "transparent",
                border: "none",
                color: "inherit",
                cursor: "pointer",
                fontSize: "var(--text-base)",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}
